import React from 'react';

import { Alert, Stack, TextField, Typography } from '@mui/material';

import CapacityStep from './CapacityStep';
import PackageSetupStepSection from './PackageSetupStep/PackageSetupStepSection';
import type { usePackageSetupStep } from './PackageSetupStep/usePackageSetupStep';
import PrerequisitesStep from './PrerequisitesStep';
import type { useOpportunityStep } from './PrerequisitesStep/useOpportunityStep';
import PropertyStep from './PropertyStep';
import type { usePropertyStep } from './PropertyStep/usePropertyStep';
import RatePlanStep from './RatePlanStep';
import type { useRatePlanStep } from './RatePlanStep/useRatePlanStep';
import RoomRatesStep from './RoomRatesStep';
import type { useRoomRatesStep } from './RoomRatesStep/useRoomRatesStep';
import RoomsStep from './RoomsStep';
import type { useRoomsStep } from './RoomsStep/useRoomsStep';
import { buildPackageRoomOptions } from './RoomsStep/utils';
import SubmissionStep from './SubmissionStep/SubmissionStep';
import { SECTION_STEP_INDEX, buildDraftSummary } from './SubmissionStep/buildDraftSummary';
import type { SubmissionSectionKey } from './SubmissionStep/buildDraftSummary';
import type { useSubmissionStep } from './SubmissionStep/useSubmissionStep';
import TaxesFeesStep from './TaxesFeesStep';
import type { useTaxesFeesStep } from './TaxesFeesStep/useTaxesFeesStep';
import { getActiveStepFlags } from './activeStepFlags';
import { STEPS, TAXES_FEES_FORM_ID } from './constants';
import type { FlashOfferProperty, SubmissionFailureActionabilityCheck } from './types';

interface Props {
  activeStep: number;
  errorMessage: string | null;
  isStepBusy: boolean;
  onAdvanceStep: () => void;
  onGoToContentPage: () => void;
  onGoToStep: (stepIndex: number) => void;
  onPropertyCreateError: () => void;
  onPropertyCreateStart: () => void;
  onPropertyCreateSuccess: (property: FlashOfferProperty) => void;
  onRetrySync: () => void;
  onSalesforceIdChange: (value: string) => void;
  opportunity: ReturnType<typeof useOpportunityStep>;
  packageSetupStep: ReturnType<typeof usePackageSetupStep>;
  propertyStep: ReturnType<typeof usePropertyStep>;
  ratePlanStep: ReturnType<typeof useRatePlanStep>;
  roomRatesStep: ReturnType<typeof useRoomRatesStep>;
  roomsStep: ReturnType<typeof useRoomsStep>;
  setErrorMessage: (message: string | null) => void;
  submissionStep: ReturnType<typeof useSubmissionStep>;
  taxesFeesStep: ReturnType<typeof useTaxesFeesStep>;
  vendorId: string;
}

/**
 * The wizard's per-step body: renders whichever step's presentational component matches
 * `activeStep`, plus the Package Setup step's linking banners and the shared error alert.
 * Each step hook is passed through whole so this stays a stable, uniform interface as steps
 * gain or lose fields, rather than growing a new prop here every time a step hook changes shape.
 */
export default function WizardStepContent({
  activeStep,
  errorMessage,
  isStepBusy,
  onAdvanceStep,
  onGoToContentPage,
  onGoToStep,
  onPropertyCreateError,
  onPropertyCreateStart,
  onPropertyCreateSuccess,
  onRetrySync,
  onSalesforceIdChange,
  opportunity,
  packageSetupStep,
  propertyStep,
  ratePlanStep,
  roomRatesStep,
  roomsStep,
  setErrorMessage,
  submissionStep,
  taxesFeesStep,
  vendorId,
}: Props) {
  const {
    isCapacityStep,
    isPackageSetupStep,
    isPrerequisitesStep,
    isPropertyStep,
    isRatePlanStep,
    isRoomRatesStep,
    isRoomStep,
    isSubmissionStep,
    isTaxesAndFeesStep,
  } = getActiveStepFlags(activeStep);
  const currentStep = STEPS[activeStep];
  // Checking whether the last failure is fixable here is just looking up the owning step and
  // calling its own method -- no central switch needs updating as failure modes are added.
  const stepFailureActionability: Record<SubmissionSectionKey, SubmissionFailureActionabilityCheck> = {
    packages: packageSetupStep,
    property: propertyStep,
    ratePlans: ratePlanStep,
    roomRates: roomRatesStep,
    rooms: roomsStep,
    taxesFees: taxesFeesStep,
  };
  const isFailureActionable =
    !!submissionStep.failedStage &&
    stepFailureActionability[submissionStep.failedStage].isSubmissionFailureActionable({
      flexibleNightsBlockedCount: submissionStep.flexibleNightsBlockedCount,
      packageUnmatchedCount: submissionStep.packageUnmatchedCount,
      updateFailurePackageIds: submissionStep.updateFailurePackageIds,
    });

  // Informational only -- shown regardless of whether this step has a fix-it path, unlike the
  // Submission review's "Go to step" CTA which checks `isFailureActionable`.
  const failedStepIndex = submissionStep.failedStage ? SECTION_STEP_INDEX[submissionStep.failedStage] : null;
  const isViewingLastFailedStep =
    !!submissionStep.submitErrorMessage && failedStepIndex !== null && activeStep === failedStepIndex;

  return (
    <Stack spacing={2}>
      <Typography variant="h6">{currentStep.title}</Typography>
      {!isPackageSetupStep && (
        <Typography variant="body2" color="text.secondary">
          {currentStep.helperText}
        </Typography>
      )}

      {/* On Rooms the message shows with the step intro, above the long room list. */}
      {isRoomStep && errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      {isViewingLastFailedStep && (
        <Alert severity="error">
          <Typography fontWeight={600}>The last submission attempt failed here:</Typography>
          <Typography variant="body2">{submissionStep.submitErrorMessage}</Typography>
        </Alert>
      )}

      {activeStep === 0 && (
        <TextField
          label="Salesforce offer ID"
          value={opportunity.salesforceOfferId}
          onChange={(event) => onSalesforceIdChange(event.target.value)}
          required
          fullWidth
          autoFocus
          disabled={isStepBusy}
        />
      )}

      {isPrerequisitesStep && (
        <PrerequisitesStep
          hasSyncFailed={opportunity.hasResyncFailed}
          isSyncing={opportunity.isWaitingForResync || opportunity.prerequisiteCheckStatus === 'checking'}
          items={opportunity.prerequisiteItems}
          onRetrySync={onRetrySync}
          syncFailureMessage={opportunity.resyncFailureMessage}
          syncProgressPercent={opportunity.resyncProgressPercent}
        />
      )}

      {isPropertyStep && (
        <PropertyStep
          activeProperties={propertyStep.activeProperties}
          hasPropertyFormError={propertyStep.hasPropertyFormError}
          isBusy={isStepBusy}
          isCreatingProperty={propertyStep.isCreatingProperty}
          isLoadingPropertyForm={propertyStep.isLoadingPropertyForm}
          onDraftChange={propertyStep.setPropertyFormData}
          onPropertyCreateError={onPropertyCreateError}
          onPropertyCreateStart={onPropertyCreateStart}
          onPropertyCreateSuccess={onPropertyCreateSuccess}
          onPropertySelect={(propertyId) => {
            setErrorMessage(null);
            propertyStep.selectProperty(propertyId);
            roomsStep.setRoomDrafts([]);
          }}
          onRetryPropertySchema={() => {
            setErrorMessage(null);
            propertyStep.refetchCreatePropertySchema();
          }}
          propertyFormData={propertyStep.propertyFormData}
          propertySchema={propertyStep.propertySchema}
          selectedPropertyId={propertyStep.selectedPropertyId}
          vendorId={vendorId}
        />
      )}

      {isRatePlanStep && (
        <RatePlanStep
          disabled={isStepBusy}
          expandedRatePlanDraftIds={ratePlanStep.expandedRatePlanDraftIds}
          hasSchemaError={ratePlanStep.hasRatePlanCreateSchemaError}
          isLoadingSchema={ratePlanStep.isLoadingRatePlanCreateSchema}
          onAddDraft={() => {
            ratePlanStep.addRatePlanDraft();
            setErrorMessage(null);
          }}
          onClearError={() => setErrorMessage(null)}
          onRemoveDraft={(draftId) => {
            ratePlanStep.removeRatePlanDraft(draftId);
            setErrorMessage(null);
          }}
          onRetrySchema={ratePlanStep.refetchRatePlanCreateSchema}
          onToggleDraftExpanded={ratePlanStep.toggleRatePlanDraftExpanded}
          onUpdateDraft={ratePlanStep.updateRatePlanDraft}
          ratePlanCreateSchema={ratePlanStep.ratePlanCreateSchema}
          ratePlanDrafts={ratePlanStep.ratePlanDrafts}
          readOnly={ratePlanStep.isRatePlanStepReadOnly}
        />
      )}

      {isRoomStep && (
        <RoomsStep
          amenityOptions={roomsStep.amenityOptions}
          hasRoomDraftFormError={roomsStep.hasRoomDraftFormError}
          isBusy={isStepBusy}
          isLoadingRoomDraftForm={roomsStep.isLoadingRoomDraftForm}
          isLoadingRoomTypesSchema={roomsStep.isLoadingRoomTypesSchema}
          onAddAmenityOption={roomsStep.handleAddAmenityOption}
          onAddRoomDraft={() => {
            const { errorMessage: addRoomError } = roomsStep.handleAddRoomDraft();

            if (addRoomError) {
              setErrorMessage(addRoomError);
            }
          }}
          onRemoveRoomDraft={roomsStep.handleRemoveRoomDraft}
          onRetryLoadRoomFields={() => {
            setErrorMessage(null);
            roomsStep.refetchRoomTypesSchema();
          }}
          onSelectAssetRoom={packageSetupStep.selectAssetRoom}
          onUpdateRoomDraft={roomsStep.updateRoomDraft}
          packageRoomOptions={buildPackageRoomOptions(roomRatesStep.roomRateRooms, roomsStep.roomDrafts)}
          property={propertyStep.selectedProperty}
          roomAmenityPrefill={roomsStep.roomAmenityPrefill}
          roomDrafts={roomsStep.roomDrafts}
          roomAssetRows={roomsStep.roomAssetRows}
          roomKeyByAssetId={packageSetupStep.assetRoomKeys}
          roomSourceByAssetId={packageSetupStep.assetRoomSources}
          roomTypesFormSchema={roomsStep.roomTypesFormSchema}
          unmatchedSalesforceRoomNames={roomsStep.unmatchedSalesforceRoomNames}
        />
      )}

      {isRoomRatesStep && (
        <RoomRatesStep
          appliedRatePlanNamesByRoomTypeId={roomRatesStep.appliedRatePlanNamesByRoomTypeId}
          disabled={isStepBusy}
          onClearRooms={() => {
            roomRatesStep.clearAllRooms();
            setErrorMessage(null);
          }}
          onSelectAllRooms={() => {
            roomRatesStep.selectAllRooms();
            setErrorMessage(null);
          }}
          onSelectRatePlans={(roomTypeId, ratePlanIds) => {
            roomRatesStep.handleSelectRoomRatePlans(roomTypeId, ratePlanIds);
            setErrorMessage(null);
          }}
          ratePlanOptions={ratePlanStep.ratePlanDrafts.map((draft) => ({
            id: draft.draftId,
            name: draft.ratePlanName,
          }))}
          readOnly={roomRatesStep.isRoomRatesStepReadOnly}
          rooms={roomRatesStep.roomRateRooms}
          selectedRatePlanIds={roomRatesStep.selectedRoomRatePlanIds}
        />
      )}

      {isCapacityStep && <CapacityStep roomRates={roomRatesStep.draftRoomRateAssignments} />}

      {isTaxesAndFeesStep && (
        <TaxesFeesStep
          disabled={isStepBusy}
          formId={TAXES_FEES_FORM_ID}
          onSaved={async () => {
            setErrorMessage(null);
            onAdvanceStep();
          }}
          propertyId={propertyStep.selectedProperty?.id}
          taxesFeesStep={taxesFeesStep}
        />
      )}

      {isPackageSetupStep && (
        <PackageSetupStepSection
          isStepBusy={isStepBusy}
          onAdvanceStep={onAdvanceStep}
          packageSetupStep={packageSetupStep}
          setErrorMessage={setErrorMessage}
        />
      )}

      {isSubmissionStep && (
        <SubmissionStep
          draftSummary={buildDraftSummary({
            flexibleNightsById: packageSetupStep.flexibleNightsById,
            matchedPackages: packageSetupStep.matchedPackages,
            packageRatePlanIds: packageSetupStep.packageRatePlanIds,
            packageRoomTypeIds: packageSetupStep.packageRoomTypeIds,
            property: propertyStep.selectedProperty,
            ratePlanDrafts: ratePlanStep.ratePlanDrafts,
            roomDrafts: roomsStep.roomDrafts,
            roomRateRooms: roomRatesStep.roomRateRooms,
            selectedRoomRatePlanIds: roomRatesStep.selectedRoomRatePlanIds,
            taxesFeesDraft: taxesFeesStep.taxesFeesDraft,
          })}
          failedStage={submissionStep.failedStage}
          hasSucceeded={submissionStep.hasSucceeded}
          isFailureActionable={isFailureActionable}
          onGoToContentPage={onGoToContentPage}
          onGoToStep={onGoToStep}
          packageLinkFailures={submissionStep.packageLinkFailures}
          rollbackErrors={submissionStep.rollbackErrors}
          submitErrorMessage={submissionStep.submitErrorMessage}
        />
      )}

      {!isRoomStep && errorMessage && <Alert severity="error">{errorMessage}</Alert>}
    </Stack>
  );
}
